import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useBrand } from "../branding/BrandContext";
import BrandText, { getBrandedPlainText } from "../branding/BrandText";

const faqSections = [
  {
    title: "Getting Started",
    items: [
      {
        question: "What is HomeHelper?",
        answer: "HomeHelper is a homeowner-first platform designed to remove the confusion, risk, and guesswork from home improvement projects. Instead of blindly searching for contractors or relying on inconsistent quotes, HomeHelper helps you understand what your project should cost, what it involves, and connects you with trusted professionals who are qualified and ready to take on your specific job. Our goal is to empower you with clarity and confidence before you spend a dollar."
      },
      {
        question: "How does HomeHelper work?",
        answer: "You start by submitting a simple project request that includes details about your home, the type of work needed, and optionally photos. From there, our system analyzes your inputs and generates a realistic cost expectation while simultaneously identifying contractors in your area who specialize in that exact type of project. Those contractors are then notified and can reach out to you directly to discuss next steps, schedule an appointment, or provide a formal quote."
      },
      {
        question: "Is HomeHelper free?",
        answer: "Yes, HomeHelper is completely free for homeowners. There are no hidden fees, subscriptions, or obligations. Our platform is supported by contractors who participate in our lead and campaign system, which allows us to provide you with high-quality matches and insights at no cost to you."
      },
      {
        question: "How do I get started?",
        answer: "Getting started takes less than a minute. Simply fill out the project form on HomeHelper.com, provide a few details about your project, and optionally upload photos. From there, we handle the heavy lifting—analyzing your project, estimating costs, and connecting you with the right professionals."
      }
    ]
  },
  {
    title: "Project Matching",
    items: [
      {
        question: "How are contractors matched?",
        answer: "We match you with contractors based on multiple factors including your project type, location (zip code), project scope, and real-time contractor availability. Our system prioritizes contractors who actively specialize in your specific type of project and who are currently operating in your area, ensuring that you are connected with professionals who are both qualified and ready to take on your job."
      },
      {
        question: "Do I get multiple quotes?",
        answer: "In many cases, yes. Depending on availability in your area, we aim to connect you with more than one qualified contractor so you can compare pricing, approach, and communication style. This gives you leverage and confidence when making your final decision."
      },
      {
        question: "How quickly will I hear back?",
        answer: "Most homeowners are contacted within minutes to a few hours after submitting their project. Our platform is designed to connect you with active contractors who are ready to engage quickly, not days later."
      },
      {
        question: "Can I choose my contractor?",
        answer: "Absolutely. You are always in full control of who you decide to work with. HomeHelper simply facilitates the connection and provides insight—you make the final decision based on your comfort, budget, and preference."
      }
    ]
  },
  {
    title: "Trust & Safety",
    items: [
      {
        question: "Are contractors licensed and insured?",
        answer: "We prioritize working with contractors who are licensed, bonded, and insured in accordance with state and local requirements. While licensing requirements vary by location, our system is designed to surface professionals who meet those standards."
      },
      {
        question: "Do you verify contractors?",
        answer: "Yes. We perform verification checks that may include license validation, insurance verification, and business legitimacy reviews. Additionally, contractor performance is continuously monitored through engagement and feedback signals to maintain quality across the platform."
      },
      {
        question: "Is my data secure?",
        answer: "Your privacy and security are extremely important to us. Your information is stored securely and only shared with contractors who are directly relevant to your project. We do not sell your personal data or distribute it broadly."
      },
      {
        question: "Will I get spammed?",
        answer: "No. Unlike traditional lead platforms that may distribute your information widely, HomeHelper limits exposure to a small number of relevant contractors. This ensures meaningful conversations instead of overwhelming outreach."
      }
    ]
  },
  {
    title: "Estimates & Planning",
    items: [
      {
        question: "Do you provide cost estimates?",
        answer: "Yes. Based on your inputs, we prepare cost estimates that reflect real-world pricing trends for your specific type of project and location. This gives you a strong starting point before speaking with contractors."
      },
      {
        question: "Are estimates accurate?",
        answer: "Our estimates are designed to provide a realistic and informed range, not an exact quote. Final pricing will depend on contractor inspection, materials, and project-specific variables, but our goal is to eliminate surprises and give you a strong baseline expectation."
      },
      {
        question: "Can I upload photos?",
        answer: "Yes, and we strongly recommend it. Photos significantly improve the accuracy of your estimate and help contractors better understand your project before reaching out."
      },
      {
        question: "Can I plan without committing?",
        answer: "Absolutely. You can use HomeHelper purely as a planning tool to understand scope and cost without any obligation to move forward."
      }
    ]
  },
  {
    title: "Marketplace & Leads",
    items: [
      {
        question: "What is an exclusive lead?",
        answer: "An exclusive lead is sent to only one contractor. This allows for a more direct and personalized experience where the contractor can focus entirely on your project without competing bids."
      },
      {
        question: "What is a marketplace lead?",
        answer: "Marketplace leads are shared with a small number of contractors, allowing you to receive multiple perspectives and competitive pricing options."
      },
      {
        question: "Do contractors pay?",
        answer: "Yes. Contractors participate through paid campaigns or lead purchases, which helps ensure that only serious, motivated professionals engage with your project."
      },
      {
        question: "How do you ensure quality?",
        answer: "We continuously evaluate contractor performance based on responsiveness, engagement, and feedback signals. Contractors who do not meet expectations can be deprioritized or removed from the platform."
      }
    ]
  }
];

const allFAQs = faqSections.flatMap(section => section.items);

export default function FAQPage() {
  const brand = useBrand();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": allFAQs.map((faq) => ({
              "@type": "Question",
              "name": getBrandedPlainText(faq.question, brand),
              "acceptedAnswer": {
                "@type": "Answer",
                "text": getBrandedPlainText(faq.answer, brand)
              }
            }))
          })
        }}
      />

      <div className="faqPage">
        <h1 className="faqPage__title">Frequently Asked Questions</h1>
        <p className="faqPage__subtitle">
          <BrandText>Everything you need to know about using HomeHelper.</BrandText>
        </p>

        {faqSections.map((section, sIndex) => (
          <div key={sIndex} className="faqPage__section">
            <h2 className="faqPage__sectionTitle">{section.title}</h2>

            {section.items.map((faq, index) => {
              const globalIndex = `${sIndex}-${index}`;
              const isOpen = openIndex === globalIndex;

              return (
                <div key={globalIndex} className="faqPage__card">
                  <div
                    className="faqPage__questionRow"
                    onClick={() => toggle(globalIndex)}
                  >
                    <h3 className="faqPage__question"><BrandText>{faq.question}</BrandText></h3>
                    <span className="faqPage__icon">{isOpen ? "−" : "+"}</span>
                  </div>

                  {isOpen && (
                    <p className="faqPage__answer"><BrandText>{faq.answer}</BrandText></p>
                  )}
                </div>
              );
            })}

            <div className="faqPage__cta">
              <h3 className="faqPage__ctaTitle">Ready to start your project?</h3>
              <p>Get clarity, pricing, and matched with trusted pros in minutes.</p>
              <button
                className="faqPage__button"
                onClick={() => (window.location.href = "/")}
              >
                Start Your Project
              </button>
            </div>
          </div>
        ))}
      </div>

      <Footer />
    </>
  );
}
